import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Smartphone, Zap, QrCode, Image as ImageIcon } from 'lucide-react';
import { FeatureCard } from './FeatureCard';
import { fadeInUp, fadeInLeft, fadeInRight, smoothTransition, springTransition } from './animations';

const features = [
  {
    id: 'feature-interactive',
    icon: Smartphone,
    title: 'Menú Digital Interactivo',
    description: 'Optimizado para ventas con navegación intuitiva',
  },
  {
    id: 'feature-design',
    icon: ImageIcon,
    title: 'Diseño Personalizado',
    description: 'Alineado con la identidad de tu marca',
  },
  {
    id: 'feature-strategy',
    icon: Zap,
    title: 'Organización Estratégica',
    description: 'Productos destacados para mayor conversión',
  },
  {
    id: 'feature-qr',
    icon: QrCode,
    title: 'Código QR',
    description: 'Listo para mesas, fácil de implementar',
  },
];

export const ProductDemo = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const headerAnimation = useMemo(() => ({
    ...fadeInUp,
    animate: inView ? fadeInUp.animate : fadeInUp.initial,
  }), [inView]);

  const leftAnimation = useMemo(() => ({
    ...fadeInLeft,
    animate: inView ? fadeInLeft.animate : fadeInLeft.initial,
  }), [inView]);

  const rightAnimation = useMemo(() => ({
    ...fadeInRight,
    animate: inView ? fadeInRight.animate : fadeInRight.initial,
  }), [inView]);

  return (
    <section ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[hsl(var(--cta-orange))]/5 rounded-full blur-3xl" />
      
      <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          {...headerAnimation}
          transition={smoothTransition}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Qué incluye <span className="text-primary">Zappien SmartMenu™</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Un sistema completo diseñado para maximizar tus ventas desde el primer día
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <motion.div
            {...leftAnimation}
            transition={springTransition}
            className="relative order-2 lg:order-1"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-[hsl(var(--cta-orange))]/20 rounded-3xl blur-2xl opacity-50" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_digital-menu-launch/artifacts/yblb69ms_%C3%9ALTIMA%20IMAGEN.jpg"
                  alt="Digital Menu Demo"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
              </div>
            </div>
          </motion.div>

          <motion.div
            {...rightAnimation}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="space-y-6 order-1 lg:order-2"
          >
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.id}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index + 0.4}
              />
            ))}

            <div className="glass-card p-6 rounded-2xl border-[hsl(var(--cta-orange))]/30 bg-[hsl(var(--cta-orange))]/5">
              <h4 className="font-bold text-lg mb-2">
                Infraestructura optimizada
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Carga rápida y funcionamiento estable
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Actualizaciones mensuales incluidas
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  12 meses de acceso completo
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Link en bio para redes sociales
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
